import { ToastContext } from "context/ToastContext";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { ProgressSpinner } from "primereact/progressspinner";
import { useContext, useMemo, useState } from "react";
import {
  useAthenaImportMutation,
  useLazyAthenaImportStatusQuery,
  IAthenaImportResult,
  IAthenaImportRowResult,
} from "store/queries/integration";
import styled from "styled-components";

type ExpandedSection =
  | "created"
  | "updated"
  | "duplicates"
  | "unmappedPayers"
  | "noFacility"
  | "noCpt"
  | "errors"
  | null;

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(4)};
  min-width: 500px;
`;

const FileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(2)};
`;

const FileLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
`;

const FileInfo = styled.span`
  font-size: 12px;
  color: var(--text-color-secondary);
`;

const SelectedFileDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${sizer(2)};
  background-color: var(--surface-ground);
  border-radius: 4px;
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(3)};
`;

const ResultsSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${sizer(2)};
`;

type SummaryCardVariant = "success" | "error" | "warning" | "info" | "neutral";

const SummaryCard = styled.div<{
  variant?: SummaryCardVariant;
  clickable?: boolean;
  expanded?: boolean;
}>`
  padding: ${sizer(2)};
  border-radius: 4px;
  text-align: center;
  cursor: ${({ clickable }) => (clickable ? "pointer" : "default")};
  transition: all 0.15s ease;
  border: 2px solid transparent;
  background-color: ${({ variant }) => {
    switch (variant) {
      case "success":
        return "var(--green-50)";
      case "error":
        return "var(--red-50)";
      case "warning":
        return "var(--yellow-50)";
      default:
        return "var(--surface-ground)";
    }
  }};

  ${({ clickable }) =>
    clickable &&
    `
    &:hover {
      filter: brightness(0.95);
    }
  `}

  ${({ expanded, variant }) => {
    if (!expanded) return "";
    const borderColors: Record<string, string> = {
      success: "var(--green-400)",
      error: "var(--red-400)",
      warning: "var(--yellow-400)",
    };
    return `border-color: ${
      (variant && borderColors[variant]) || "var(--primary-400)"
    };`;
  }}

  .count {
    font-size: 24px;
    font-weight: bold;
    color: ${({ variant }) => {
      switch (variant) {
        case "success":
          return "var(--green-600)";
        case "error":
          return "var(--red-600)";
        case "warning":
          return "var(--yellow-600)";
        default:
          return "var(--text-color)";
      }
    }};
  }

  .label {
    font-size: 12px;
    color: var(--text-color-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  .expand-icon {
    font-size: 10px;
  }
`;

const DetailItem = styled.div`
  padding: ${sizer(2)};
  border-bottom: 1px solid var(--surface-border);
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${sizer(2)};

  &:last-child {
    border-bottom: none;
  }

  .patient-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .patient-name {
    font-weight: 600;
  }

  .dos {
    color: var(--text-color-secondary);
    font-size: 12px;
  }

  .account-name {
    color: var(--text-color-secondary);
    font-size: 11px;
    font-style: italic;
  }

  .error-message {
    color: var(--red-600);
    font-size: 12px;
    max-width: 300px;
    word-break: break-word;
  }
`;

const DetailList = styled.div`
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  background-color: var(--surface-ground);
`;

const DetailSection = styled.div`
  margin-top: ${sizer(2)};
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${sizer(1)};

  .section-title {
    font-weight: 600;
    font-size: 13px;
    color: var(--text-color-secondary);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${sizer(8)};
  gap: ${sizer(2)};
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${sizer(2)};
  padding: ${sizer(3)};
  background-color: var(--blue-50);
  border: 1px solid var(--blue-200);
  border-radius: 4px;
  font-size: 13px;
  color: var(--blue-700);

  .pi-info-circle {
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

interface IAthenaImportDialogProps {
  visible: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

export function AthenaImportDialog({
  visible,
  onHide,
  onSuccess,
}: IAthenaImportDialogProps) {
  const { current: toastElement } = useContext(ToastContext);
  const [athenaFile, setAthenaFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<IAthenaImportResult | null>(
    null
  );
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>(null);
  // CLE-2633 — the import runs server-side now, so the dialog polls rather than awaits.
  const [isPolling, setIsPolling] = useState(false);
  const [fetchStatus] = useLazyAthenaImportStatusQuery();

  const [athenaImport, { isLoading }] = useAthenaImportMutation();

  const categorizedResults = useMemo(() => {
    if (!importResult?.results) {
      return {
        created: [],
        updated: [],
        duplicates: [],
        unmappedPayers: [],
        noFacility: [],
        noCpt: [],
        errors: [],
      };
    }

    const created: IAthenaImportRowResult[] = [];
    const updated: IAthenaImportRowResult[] = [];
    const duplicates: IAthenaImportRowResult[] = [];
    const unmappedPayers: IAthenaImportRowResult[] = [];
    const noFacility: IAthenaImportRowResult[] = [];
    const noCpt: IAthenaImportRowResult[] = [];
    const errors: IAthenaImportRowResult[] = [];

    importResult.results.forEach((row) => {
      if (row.success && row.updated) {
        updated.push(row);
      } else if (row.success && !row.updated) {
        created.push(row);
      } else if (
        row.skipped &&
        (row.errors?.ALREADY_CURRENT || row.errors?.DUPLICATE)
      ) {
        duplicates.push(row);
      } else if (row.errors?.UNMAPPED_PAYER) {
        unmappedPayers.push(row);
      } else if (row.errors?.NO_FACILITY) {
        // CLE-2633 — these are deliberate skips, not failures. Without their own buckets they
        // landed in `errors`, so the drill-down disagreed with the Errors count from the API.
        noFacility.push(row);
      } else if (row.errors?.NO_CPT) {
        noCpt.push(row);
      } else if (!row.success && row.errors) {
        errors.push(row);
      }
    });

    return {
      created,
      updated,
      duplicates,
      unmappedPayers,
      noFacility,
      noCpt,
      errors,
    };
  }, [importResult?.results]);

  const handleFileSelect = (e: { files: File[] }) => {
    if (e.files && e.files.length > 0) {
      setAthenaFile(e.files[0]);
    }
  };

  /**
   * Poll the import row until it reaches a terminal status.
   *
   * There is no client-side timeout: a large report legitimately takes minutes, and an import that
   * dies with its container is swept to `failed` by a server-side cron, so polling always ends.
   */
  const pollUntilFinished = async (importId: string) => {
    const intervalMs = 3000;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await fetchStatus(importId).unwrap();
      if (data.status === "completed" || data.status === "failed") return data;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, intervalMs);
      });
    }
  };

  const handleImport = async () => {
    if (!athenaFile) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a file before importing",
      });
      return;
    }

    try {
      // The upload answers 202 with an id; the work continues server-side. Aptible's nginx closes
      // the connection at 60s, so awaiting the whole import here is what produced the 504.
      const started = await athenaImport({
        athenaReport: athenaFile,
      }).unwrap();

      setIsPolling(true);
      const result = { data: await pollUntilFinished(started.data.importId) };
      setIsPolling(false);

      if (result.data.status === "failed") {
        toastElement?.show({
          severity: "error",
          summary: "Import Failed",
          detail: result.data.errorMessage || "An error occurred during import",
        });
        return;
      }

      setImportResult(result.data);

      if (result.data.successCount > 0 || result.data.updateCount > 0) {
        const parts = [];
        if (result.data.successCount > 0) {
          parts.push(`created ${result.data.successCount}`);
        }
        if (result.data.updateCount > 0) {
          parts.push(`updated ${result.data.updateCount}`);
        }
        toastElement?.show({
          severity: "success",
          summary: "Import Complete",
          detail: `Successfully ${parts.join(" and ")} orders`,
        });
        onSuccess?.();
      }

      if (result.data.errorCount > 0 || result.data.unmappedPayerCount > 0) {
        const errorParts = [];
        if (result.data.errorCount > 0) {
          errorParts.push(`${result.data.errorCount} errors`);
        }
        if (result.data.unmappedPayerCount > 0) {
          errorParts.push(`${result.data.unmappedPayerCount} unmapped payers`);
        }
        toastElement?.show({
          severity: "warn",
          summary: "Import Completed with Issues",
          detail: errorParts.join(", "),
        });
      }
    } catch (error: any) {
      setIsPolling(false);
      toastElement?.show({
        severity: "error",
        summary: "Import Failed",
        detail: error.data?.message || "An error occurred during import",
      });
    }
  };

  const handleClose = () => {
    setAthenaFile(null);
    setImportResult(null);
    setExpandedSection(null);
    onHide();
  };

  const toggleSection = (section: ExpandedSection) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const canImport = athenaFile && !isLoading && !isPolling;

  const footerContent = (
    <div className="flex justify-content-end gap-2">
      <Button
        label={importResult ? "Close" : "Cancel"}
        onClick={handleClose}
        className="p-button-text"
      />
      {!importResult && (
        <Button
          label="Import"
          onClick={handleImport}
          disabled={!canImport}
          loading={isLoading || isPolling}
        />
      )}
    </div>
  );

  const renderSummaryCard = (
    label: string,
    count: number,
    variant: SummaryCardVariant,
    section: ExpandedSection,
    hasItems: boolean
  ) => (
    <SummaryCard
      variant={variant}
      clickable={hasItems}
      expanded={expandedSection === section}
      onClick={() => hasItems && toggleSection(section)}
    >
      <div className="count">{count}</div>
      <div className="label">
        {label}
        {hasItems && (
          <i
            className={`pi pi-chevron-${
              expandedSection === section ? "up" : "down"
            } expand-icon`}
          />
        )}
      </div>
    </SummaryCard>
  );

  const renderDetailList = (
    title: string,
    rows: IAthenaImportRowResult[],
    showErrors?: boolean
  ) => (
    <DetailSection>
      <DetailHeader>
        <span className="section-title">
          {title} ({rows.length})
        </span>
      </DetailHeader>
      <DetailList>
        {rows.map((row, index) => (
          <DetailItem key={index}>
            <div className="patient-info">
              <span className="patient-name">{row.patientName}</span>
              <span className="dos">DOS: {row.dos}</span>
              <span className="account-name">{row.accountName}</span>
              {showErrors && row.errors && (
                <span className="error-message">
                  {Object.values(row.errors).join("; ")}
                </span>
              )}
            </div>
          </DetailItem>
        ))}
      </DetailList>
    </DetailSection>
  );

  const renderDialogContent = () => {
    if (isLoading || isPolling) {
      return (
        <LoadingContainer>
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <span>
            Importing orders. A large report can take a few minutes — you can
            leave this open.
          </span>
        </LoadingContainer>
      );
    }

    if (importResult) {
      const hasCreated = categorizedResults.created.length > 0;
      const hasUpdated = categorizedResults.updated.length > 0;
      const hasDuplicates = categorizedResults.duplicates.length > 0;
      const hasUnmappedPayers = categorizedResults.unmappedPayers.length > 0;
      const hasNoFacility = categorizedResults.noFacility.length > 0;
      const hasNoCpt = categorizedResults.noCpt.length > 0;
      const hasErrors = categorizedResults.errors.length > 0;

      return (
        <ResultsContainer>
          <ResultsSummary>
            <SummaryCard variant="info">
              <div className="count">{importResult.totalGroups}</div>
              <div className="label">Groups</div>
            </SummaryCard>
            {renderSummaryCard(
              "Created",
              importResult.successCount,
              "success",
              "created",
              hasCreated
            )}
            {renderSummaryCard(
              "Updated",
              importResult.updateCount,
              "success",
              "updated",
              hasUpdated
            )}
            {renderSummaryCard(
              "Duplicates",
              importResult.duplicateCount,
              "warning",
              "duplicates",
              hasDuplicates
            )}
            {renderSummaryCard(
              "Unmapped",
              importResult.unmappedPayerCount,
              "error",
              "unmappedPayers",
              hasUnmappedPayers
            )}
            {renderSummaryCard(
              "No facility",
              importResult.noFacilityCount,
              "warning",
              "noFacility",
              hasNoFacility
            )}
            {renderSummaryCard(
              "No CPT",
              importResult.noCptCount,
              "warning",
              "noCpt",
              hasNoCpt
            )}
            {renderSummaryCard(
              "Errors",
              importResult.errorCount,
              "error",
              "errors",
              hasErrors
            )}
          </ResultsSummary>

          {expandedSection === "created" &&
            hasCreated &&
            renderDetailList("Created Orders", categorizedResults.created)}

          {expandedSection === "updated" &&
            hasUpdated &&
            renderDetailList("Updated Orders", categorizedResults.updated)}

          {expandedSection === "duplicates" &&
            hasDuplicates &&
            renderDetailList("Duplicate Orders", categorizedResults.duplicates)}

          {expandedSection === "unmappedPayers" &&
            hasUnmappedPayers &&
            renderDetailList(
              "Unmapped Payers",
              categorizedResults.unmappedPayers,
              true
            )}

          {expandedSection === "noFacility" &&
            hasNoFacility &&
            renderDetailList(
              "Skipped - no sending facility",
              categorizedResults.noFacility,
              true
            )}

          {expandedSection === "noCpt" &&
            hasNoCpt &&
            renderDetailList(
              "Skipped - no CPT code",
              categorizedResults.noCpt,
              true
            )}

          {expandedSection === "errors" &&
            hasErrors &&
            renderDetailList("Errors", categorizedResults.errors, true)}

          {(importResult.successCount > 0 || importResult.updateCount > 0) && (
            <InfoBox>
              <i className="pi pi-info-circle" />
              <span>
                Eligibility checks are running in the background. Orders will
                show updated benefit information once processing is complete.
              </span>
            </InfoBox>
          )}

          {importResult.totalRows !== importResult.totalGroups && (
            <FileInfo>
              Note: {importResult.totalRows} rows were collapsed into{" "}
              {importResult.totalGroups} patient groups (multiple CPT codes per
              patient).
            </FileInfo>
          )}
        </ResultsContainer>
      );
    }

    return (
      <DialogContent>
        <FileSection>
          <FileLabel>Athena Scheduling Report</FileLabel>
          <FileInfo>
            Upload the Clear Health Reporting file from Athena (.xlsx or .csv)
          </FileInfo>
          {athenaFile ? (
            <SelectedFileDisplay>
              <span>{athenaFile.name}</span>
              <Button
                icon="pi pi-times"
                className="p-button-text p-button-sm"
                onClick={() => setAthenaFile(null)}
              />
            </SelectedFileDisplay>
          ) : (
            <FileUpload
              mode="basic"
              accept=".csv,.xlsx"
              maxFileSize={10000000}
              customUpload
              auto
              uploadHandler={handleFileSelect}
              chooseLabel="Select File"
            />
          )}
        </FileSection>
      </DialogContent>
    );
  };

  return (
    <Dialog
      header="Import Orders from Athena"
      visible={visible}
      onHide={handleClose}
      footer={footerContent}
      style={{ width: importResult ? "700px" : "auto" }}
    >
      {renderDialogContent()}
    </Dialog>
  );
}
